import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { DynamicMuiIcon } from '../../shared/components/DynamicMuiIcon';
import { StatusChip } from '../../shared/components/StatusChip';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { AiAssistantRole, AiAssistantRoleTool, DocumentCategory } from '../../core/types/data-models';
import { AiAssistantRoleDialog } from './AiAssistantRoleDialog';
import { AiAssistantRoleToolDialog } from './AiAssistantRoleToolDialog';

const DEFAULT_CARD_COLOR = '#64748B';

/** Picks black/white text for readability against an arbitrary background hex. */
function getContrastText(hex: string): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((v) => Number.isNaN(v))) return '#fff';
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? 'rgba(0,0,0,0.87)' : '#fff';
}

interface RoleCardProps {
  role: AiAssistantRole;
  categoryName: (type: string) => string;
  onClick: () => void;
}

function RoleCard({ role, categoryName, onClick }: RoleCardProps) {
  const bg = role.color || DEFAULT_CARD_COLOR;
  const text = getContrastText(bg);
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: 2,
        p: 2,
        bgcolor: bg,
        color: text,
        boxShadow: 1,
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {role.logoImage ? (
            <Box component="img" src={role.logoImage} alt={role.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <DynamicMuiIcon iconName={role.avatarIcon} sx={{ color: text, fontSize: 36 }} />
          )}
        </Box>
        <Chip
          label={role.status}
          size="small"
          sx={{
            height: 20,
            fontSize: 11,
            bgcolor: 'rgba(255,255,255,0.25)',
            color: text,
          }}
        />
      </Stack>
      <Typography variant="subtitle1" fontWeight={700} noWrap>
        {role.name}
      </Typography>
      <Typography
        variant="caption"
        sx={{ opacity: 0.85, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.4em', mt: 0.25 }}
      >
        {role.description}
      </Typography>
      <Chip
        label={categoryName(role.documentCategoryType)}
        size="small"
        variant="outlined"
        sx={{ mt: 1.5, height: 22, fontSize: 11, borderColor: 'rgba(255,255,255,0.5)', color: text }}
      />
    </Box>
  );
}

interface RoleGridProps {
  roles: AiAssistantRole[];
  categoryName: (type: string) => string;
  onCardClick: (role: AiAssistantRole) => void;
}

function RoleGrid({ roles, categoryName, onCardClick }: RoleGridProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 2,
      }}
    >
      {roles.map((role) => (
        <RoleCard key={role.id} role={role} categoryName={categoryName} onClick={() => onCardClick(role)} />
      ))}
    </Box>
  );
}

export function AiAssistantRolesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: roleEnv, isLoading, error } = useQuery({
    queryKey: ['aiAssistantRoles'],
    queryFn: () => resources.aiAssistantRoles.list({ page: 1, pageSize: 200, sort: 'order' }),
  });
  const { data: catEnv } = useQuery({
    queryKey: ['documentCategories'],
    queryFn: () => resources.documentCategories.list({ page: 1, pageSize: 200, sort: 'order' }),
  });
  // Unfiltered — narrowed client-side to default (assistantRoleKey null) tools
  // below, since "no role" can't be expressed as a single equality filter.
  const { data: toolsEnv, isLoading: loadingTools } = useQuery({
    queryKey: ['aiAssistantRoleTools'],
    queryFn: () => resources.aiAssistantRoleTools.list({ page: 1, pageSize: 200, sort: 'order' }),
  });

  const categories: DocumentCategory[] = catEnv?.data ?? [];
  const categoryName = (type: string) => categories.find((c) => c.type === type)?.name ?? type;

  const sortRoles = (list: AiAssistantRole[]) =>
    list.slice().sort(
      (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity) || a.name.localeCompare(b.name),
    );

  const allRoles: AiAssistantRole[] = roleEnv?.data ?? [];
  const activeRoles = sortRoles(allRoles.filter((r) => r.status === 'Active'));
  const inactiveRoles = sortRoles(allRoles.filter((r) => r.status !== 'Active'));

  const defaultTools: AiAssistantRoleTool[] = (toolsEnv?.data ?? [])
    .filter((t) => t.assistantRoleKey === null)
    .slice()
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [toolDialog, setToolDialog] = useState<{ open: boolean; editing: AiAssistantRoleTool | null }>({ open: false, editing: null });
  const [deleteToolTarget, setDeleteToolTarget] = useState<AiAssistantRoleTool | null>(null);
  const goToRole = (role: AiAssistantRole) => navigate(`/global-settings/ai-assistant-roles/${role.id}`);

  const deleteTool = useMutation({
    mutationFn: (t: AiAssistantRoleTool) => resources.aiAssistantRoleTools.remove(t.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aiAssistantRoleTools'] });
      setDeleteToolTarget(null);
    },
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error">{extractErrorMessage(error)}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="AI Assistant Roles"
        description="Manage the roster of role-based AI Assistant personas, each with its own Skills and Tools."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            New AI Assistant Role
          </Button>
        }
      />

      {activeRoles.length === 0 && inactiveRoles.length === 0 ? (
        <Alert severity="info">No AI Assistant Roles yet. Click "New AI Assistant Role" to get started.</Alert>
      ) : (
        <>
          {activeRoles.length > 0 && (
            <RoleGrid roles={activeRoles} categoryName={categoryName} onCardClick={goToRole} />
          )}

          {activeRoles.length > 0 && inactiveRoles.length > 0 && <Divider sx={{ my: 3 }} />}

          {inactiveRoles.length > 0 && (
            <RoleGrid roles={inactiveRoles} categoryName={categoryName} onCardClick={goToRole} />
          )}
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Default Tools — assistantRoleKey null, automatically available to every role */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Default Tools</Typography>
          <Typography variant="body2" color="text.secondary">
            Tools not attached to any specific role — automatically available to every AI Assistant Role.
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setToolDialog({ open: true, editing: null })}>
          New Default Tool
        </Button>
      </Stack>
      {loadingTools ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress size={24} /></Box>
      ) : defaultTools.length === 0 ? (
        <Alert severity="info">No default tools yet. Click "New Default Tool" to add one.</Alert>
      ) : (
        <Box>
          {defaultTools.map((t) => (
            <Box key={t.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1, px: 2, py: 1, '&:hover .row-actions': { opacity: 1 } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip label={t.order ?? '—'} size="small" variant="outlined" sx={{ minWidth: 32 }} />
                <BuildIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={500} sx={{ flex: 1, fontFamily: 'monospace' }}>{t.toolName}</Typography>
                <StatusChip value={t.status} />
                <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                  <Tooltip title="Edit tool">
                    <IconButton size="small" onClick={() => setToolDialog({ open: true, editing: t })}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete tool">
                    <IconButton size="small" color="error" onClick={() => setDeleteToolTarget(t)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      <AiAssistantRoleDialog
        open={dialogOpen}
        existingRoles={allRoles}
        onClose={() => setDialogOpen(false)}
      />
      <AiAssistantRoleToolDialog
        open={toolDialog.open}
        initial={toolDialog.editing}
        onClose={() => setToolDialog({ open: false, editing: null })}
      />
      <ConfirmDialog
        open={deleteToolTarget !== null}
        title="Delete Default Tool"
        message={`Are you sure you want to delete "${deleteToolTarget?.toolName}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteTool.isPending}
        onConfirm={() => deleteToolTarget && deleteTool.mutate(deleteToolTarget)}
        onClose={() => setDeleteToolTarget(null)}
      />
    </Box>
  );
}
